import React, { useMemo } from 'react';
// @ts-ignore
import { convertArrayToCSV } from 'convert-array-to-csv';
import FileSaver from 'file-saver';
import capitalize from 'lodash/capitalize';

import { LoadingButton, Appearance } from '@atlaskit/button';
import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { TransactionAccount } from '@css-xp/graphtypes/src/generated-types-new';

import { getFormattedDateString } from '../../../../index';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';

import { getBilledForColumnData, isActiveSubscription } from '../../../../entitlement-utils/src/utils';
import {
  fetchProductAppSubscriptionsForTransactionAccount,
  fetchProductAppSubscriptionsForBillingProfile,
} from '../../../../product-app-subscriptions-container';

import { useTransactionAccountContext } from '../../../../common-context';
import { getPaymentMethodDetailsText } from './util';

export const csvHeaders = [
  'Subscription',
  'Site',
  'Entitlement',
  'Edition',
  'Billing status',
  'Usage',
  'Next bill/ Expiry',
  'Billing cycle',
  'Payment method',
];

export type CloudSiteProducts = generatedTypesNew.ProductAppSubscriptionForBillingProfileFragment['products'];

export type CloudSiteProduct = generatedTypesNew.InvoiceGroupProduct | null;

export const getProductUsageDetails = (product: CloudSiteProduct) => {
  if (product?.activeUsers !== undefined && product?.activeUsers !== null) {
    return `${capitalize(getBilledForColumnData(product?.productId, product?.edition))} ${product?.activeUsers}`;
  }
  return '-';
};

export const getNextBillExpiryDate = (product: CloudSiteProduct): string => {
  if (product?.latestPeriodEndDate && isActiveSubscription(product?.billingStatus, product?.provisioningStatus)) {
    return getFormattedDateString({ date: product?.latestPeriodEndDate, format: NEW_DATE_FORMAT }) ?? '-';
  }
  return '-';
};

export const getPaymentMethodOnSubscription = (
  product: CloudSiteProduct,
  transactionAccount: TransactionAccount,
  isIGFallbackDisabled?: boolean
): string => {
  return getPaymentMethodDetailsText({
    invoiceGroupId: product?.activeSubscriptionBillingGroupId ?? product?.billingGroupId,
    transactionAccount: transactionAccount,
    isIGFallbackDisabled: isIGFallbackDisabled,
  });
};

export const getTransformedProductDetails = (
  product: CloudSiteProduct,
  transactionAccount: TransactionAccount
): string[] => {
  return [
    // col 1: Subscription base product name
    product?.baseProduct ?? '-',
    // col 2: cloud site name
    product?.cloudSiteHostname ?? '-',
    // col 3: entitlement Number (Slug) info:
    product?.entitlementNumber ?? '-',
    // col 4: edition : Premium, Standard, Free
    product?.edition ?? '-',
    // col 5: billing Status: Active / Deactivated
    capitalize(product?.billingStatus ?? '') ?? '-',
    // col 6: product usage details in users / tiers / units
    getProductUsageDetails(product),
    // col 7: Next bill / Expiry date
    getNextBillExpiryDate(product),
    // col 8: Next bill / Expiry date
    product?.billingFrequency ? capitalize(product.billingFrequency) : '-',
    // col 9: Payment method
    getPaymentMethodOnSubscription(product, transactionAccount),
  ];
};

export interface DownloadSubscriptionToCsvProps {
  isDisabled?: boolean;
  transactionAccountId?: string;
  billingProfileId?: string;
  cloudSiteIds: string[];
  appearance?: Appearance;
  text?: string;
  className?: string;
}

export const DownloadSubscriptionToCsv: React.FC<DownloadSubscriptionToCsvProps> = ({
  isDisabled,
  transactionAccountId,
  billingProfileId,
  cloudSiteIds,
  appearance = 'primary',
  text,
  className,
}) => {
  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = billingProfileId ? null : getTransactionAccount(transactionAccountData);

  const filterOptions =
    cloudSiteIds.length < 1
      ? { key: 'isNull', value: 'cloudSiteId' }
      : { key: 'cloudSiteIdIsIn', values: cloudSiteIds };

  const { data, loading } = useQuery<
    generatedTypesNew.FetchProductAppSubscriptionsForTransactionAccountQuery,
    generatedTypesNew.FetchProductAppSubscriptionsForTransactionAccountQueryVariables
  >(fetchProductAppSubscriptionsForTransactionAccount, {
    variables: {
      id: transactionAccountId as string,
      filterOptions,
    },
    skip: !transactionAccountId,
    nextFetchPolicy: 'cache-and-network',
  });

  const { data: productAppForBillingProfileData, loading: productAppForBillingProfileLoading } = useQuery<
    generatedTypesNew.FetchProductAppSubscriptionsForBillingProfileQuery,
    generatedTypesNew.FetchProductAppSubscriptionsForBillingProfileQueryVariables
  >(fetchProductAppSubscriptionsForBillingProfile, {
    variables: {
      id: billingProfileId as string,
      filterOptions,
    },
    skip: !billingProfileId,
    nextFetchPolicy: 'cache-and-network',
  });

  const result = useMemo(
    () =>
      transactionAccountId
        ? data?.transactionAccount?.productAndAppSubscriptions
        : productAppForBillingProfileData?.invoiceGroup?.productAndAppSubscriptions,
    [transactionAccountId, data, productAppForBillingProfileData]
  );

  const onDownloadClick = (): void => {
    const formattedSubscriptionData: string[][] = [csvHeaders];
    if (result && Object.keys(result).length > 0) {
      Object.keys(result).map((key: string) => {
        //@ts-ignore
        const products: CloudSiteProducts = result[key].products;
        if (products?.length > 0) {
          products?.forEach((product) => {
            // typecasting it here because type in getTransactionAccount is MayBe<TransactionAccount>
            formattedSubscriptionData.push(
              getTransformedProductDetails(product, transactionAccount as TransactionAccount)
            );
          });
        }
      });
    }
    const csv = convertArrayToCSV(formattedSubscriptionData);
    const csvFile = new Blob([csv], { type: 'text/csv' });
    FileSaver.saveAs(csvFile, 'subscriptions.csv');
  };

  return (
    <>
      <LoadingButton
        appearance={appearance}
        type="button"
        className={className}
        testId={loading || productAppForBillingProfileLoading ? 'csv-loading-button' : 'download-csv-button'}
        isDisabled={isDisabled}
        onClick={onDownloadClick}
        isLoading={loading || productAppForBillingProfileLoading}
      >
        {text || 'Download CSV'}
      </LoadingButton>
    </>
  );
};

export default DownloadSubscriptionToCsv;
